import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { getZoneById, createZone, updateZone, getCountries } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import SelectBox from "../components/UI/SelectBox";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import EntityTranslationsButton from "../components/UI/EntityTranslationsButton";
import { useTranslation } from "../context/I18nContext";

export default function AddEditZone() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("token");
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("zones");

  // Zones.js passes the exact list URL (path + query string, including its
  // active country filter/search/page) it was on when the user clicked
  // Edit/Add, via router state — see its `returnTo` navigate() calls. Using
  // that instead of a hardcoded "/zones" is what makes the filter survive
  // this round trip; falling back to a bare "/zones" keeps this page working
  // if it's ever reached some other way (a direct URL, a future caller that
  // doesn't pass this state).
  const returnTo = location.state?.returnTo || "/zones";

  const [countries, setCountries] = useState([]);
  const [form, setForm] = useState({ name: "", ascii_name: "", code: "", country_id: "" });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    getCountries(token).then(setCountries).catch(() => notify.error(t("notify_load_countries_failed", "Failed to load countries")));

    if (id) {
      getZoneById(token, id).then((z) => {
        setForm({
          name: z.name || "",
          ascii_name: z.ascii_name || "",
          code: z.code || "",
          country_id: z.country_id ? String(z.country_id) : "",
        });
      }).catch(() => notify.error(t("notify_load_failed", "Failed to load zone")));
    }
  }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const countryOptions = countries.map((c) => ({ value: String(c.id), label: c.name }));

  const setField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async () => {
    setErrors({});
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = t("validation_name_required", "Zone name is required");
    if (!form.country_id) newErrors.country_id = t("validation_country_required", "Country is required");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      notify.error(tCommon("validation_fill_required_fields", "Please fill in the required fields correctly"));
      return;
    }

    setLoading(true);
    const payload = {
      name: form.name.trim(),
      ascii_name: form.ascii_name.trim(),
      code: form.code.trim(),
      country_id: Number(form.country_id),
    };

    try {
      if (id) {
        await updateZone(token, id, payload);
        notify.success(t("notify_updated", "Zone updated successfully"));
      } else {
        await createZone(token, payload);
        notify.success(t("notify_created", "Zone created successfully"));
      }
      navigate(returnTo);
    } catch (err) {
      notify.error(err.data?.error || err.message || tCommon("error_generic", "Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate(returnTo)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">{id ? t("edit_title", "Edit Zone") : t("add_title", "Add New Zone")}</h2>
          <p className="text-gray-400 text-sm italic font-medium">{t("form_subtitle", "Define a region/governorate within a country")}</p>
        </div>
      </PageHeader>

      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div className="space-y-6">
          <div>
            <SelectBox
              label={t("field_country_label", "Country")}
              options={countryOptions}
              value={form.country_id}
              onChange={(val) => {
                setForm((prev) => ({ ...prev, country_id: val }));
                if (errors.country_id) setErrors((prev) => ({ ...prev, country_id: undefined }));
              }}
            />
            {errors.country_id && <p className="text-[10px] text-red-500 font-bold mt-1 ms-2 uppercase tracking-tight">{errors.country_id}</p>}
          </div>

          <div className="flex items-end gap-3">
            <div className="flex-grow">
              <FloatingInput label={t("field_name_label", "Zone Name")} value={form.name} onChange={setField("name")} error={errors.name} icon={ICONS.Zones} />
            </div>
            <EntityTranslationsButton entityType="zone" entityId={id} defaultName={form.name} />
          </div>
          <FloatingInput label={t("field_ascii_name_label", "ASCII Name")} value={form.ascii_name} onChange={setField("ascii_name")} />
          <FloatingInput label={t("field_code_label", "Code")} value={form.code} onChange={setField("code")} />

          <div className="flex gap-4 pt-6 border-t border-gray-50">
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              className="flex-grow shadow-lg shadow-blue-500/10"
              icon={id ? ICONS.Save : ICONS.Plus}
            >
              {id ? t("btn_update_zone", "Update Zone") : t("btn_create_zone", "Create Zone")}
            </Button>
            <Button variant="secondary" onClick={() => navigate(returnTo)}>{tCommon("btn_cancel", "Cancel")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
